import Cookies from 'js-cookie';
import FetchData from '../Components/ServerCall/FetchData';

// --- TYPES ---
export interface ISSOFormData {
  idpType: string;
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
}

export interface ISSODetailsResponse {
  idpType?: string;
  provider?: string;
  discoveryUrl?: string;
  clientId?: string;
  clientSecret?: string;
  verified?: boolean;
  sso?: 'enabled' | 'disabled' | null;
  tenantId?: string;
}

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${Cookies.get('authToken') || localStorage.getItem('accessToken')}`,
  'X-TENANT-ID': 'metadata',
};

/**
 * Fetches current SSO Configuration details.
 */
export async function getSSODetails() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'GET',
    url: 'admin/sso/details',
    headers: COMMON_HEADERS,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error fetching SSO details') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Details fetched successfully',
    data: response.data as ISSODetailsResponse,
  };
}

/**
 * Saves a new SSO Configuration (First time setup).
 */
export async function saveSSOConfig(data: ISSOFormData) {
  const response = await FetchData({
    requestType: 'withData',
    method: 'POST',
    url: 'admin/sso/save',
    headers: COMMON_HEADERS,
    data: data,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error saving configuration') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Configuration saved successfully',
    data: response.data,
  };
}

/**
 * Updates an existing SSO Configuration.
 */
export async function updateSSOConfig(data: ISSOFormData) {
  const response = await FetchData({
    requestType: 'withData',
    method: 'PUT',
    url: 'admin/sso/update',
    headers: COMMON_HEADERS,
    data: data,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error updating configuration') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Configuration updated successfully',
    data: response.data,
  };
}

/**
 * [EDIT MODE ONLY]
 * Resets verification status so configuration can be edited.
 */
export async function resetSSOVerification() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'PUT',
    url: 'admin/sso/disable',
    headers: COMMON_HEADERS,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error resetting configuration') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Configuration unlocked for editing',
    data: response.data,
  };
}

/**
 * Enables SSO for the organization.
 */
export async function enableSSO() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'POST',
    url: 'admin/sso/enabled',
    headers: COMMON_HEADERS,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error enabling SSO') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'SSO enabled successfully',
    data: response.data,
  };
}

/**
 * disables SSO for the organization.
 */

export async function disableSSO() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'POST',
    url: 'admin/sso/disabled',
    headers: COMMON_HEADERS,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error disabling SSO') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'SSO disabled successfully',
    data: response.data,
  };
}

/**
 * Fetches the SCIM Token.
 */
export async function getScimToken() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'GET',
    url: 'admin/sso/scim/token',
    headers: COMMON_HEADERS,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error fetching SCIM token') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Token generated successfully',
    data: response.data,
  };
}
